// src/services/crossroadsMultiplayerManager.ts
import { logger } from '../utils/logger';
// Comprehensive multiplayer game manager for Crossroads TCG

import { supabase } from './supabase';
import { GameState, DeckDefinition } from '../components/games/crossroads/types';
import {
  createGameSession,
  updateGameState,
  getGameSession,
  completeGame,
  subscribeToGameUpdates,
  unsubscribeFromGameUpdates,
  GameSession,
} from './crossroadsGameService';

export interface MultiplayerGameInvitation {
  id: string;
  from_user_id: string;
  from_username: string;
  from_display_name: string;
  to_user_id: string;
  deck_type: string;
  game_type: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  created_at: string;
  expires_at: string;
}

export interface ActiveMultiplayerGame {
  gameId: string;
  opponentId: string;
  opponentName: string;
  playerIndex: number;
  isMyTurn: boolean;
  lastActionAt: string;
}

/**
 * Create a new multiplayer game session
 */
export async function createMultiplayerGame(
  player1Id: string,
  player2Id: string,
  player1Deck: DeckDefinition,
  player2Deck: DeckDefinition,
  player1Name: string,
  player2Name: string
): Promise<{ success: boolean; gameId?: string; error?: string }> {
  try {
    // Import GameLogic to create initial state
    const { GameLogic } = await import('../components/games/crossroads/gameLogic');
    const { ENCHANTED_FOREST_DECK } = await import('../components/games/crossroads/environmentDecks');

    // Create initial game state
    const initialState = GameLogic.createInitialGameState(
      player1Deck,
      player2Deck,
      ENCHANTED_FOREST_DECK,
      player1Name,
      player2Name
    );

    // Create game session in database
    const result = await createGameSession(player1Id, player2Id, initialState);

    if (result.success && result.gameId) {
      return { success: true, gameId: result.gameId };
    }

    return { success: false, error: result.error };
  } catch (error) {
    logger.error('Error creating multiplayer game:', error);
    return { success: false, error: 'Failed to create multiplayer game' };
  }
}

/**
 * Join an existing multiplayer game
 */
export async function joinMultiplayerGame(
  gameId: string
): Promise<{ success: boolean; gameState?: GameState; playerIndex?: number; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Get game session
    const gameResult = await getGameSession(gameId);
    if (!gameResult.success || !gameResult.game) {
      return { success: false, error: 'Game not found' };
    }

    // Get player index
    const { data: playerData, error: playerError } = await supabase
      .from('crossroads_game_players')
      .select('player_index')
      .eq('game_id', gameId)
      .eq('user_id', user.id)
      .single();

    if (playerError || !playerData) {
      return { success: false, error: 'Player not in this game' };
    }

    return {
      success: true,
      gameState: gameResult.game.game_state as GameState,
      playerIndex: playerData.player_index,
    };
  } catch (error) {
    logger.error('Error joining multiplayer game:', error);
    return { success: false, error: 'Failed to join game' };
  }
}

/**
 * Update multiplayer game state
 */
export async function updateMultiplayerGameState(
  gameId: string,
  newState: GameState
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Verify it's the player's turn
    const { data: playerData } = await supabase
      .from('crossroads_game_players')
      .select('player_index')
      .eq('game_id', gameId)
      .eq('user_id', user.id)
      .single();

    if (!playerData) {
      return { success: false, error: 'Not a player in this game' };
    }

    // Update game state
    const result = await updateGameState(gameId, newState);
    return result;
  } catch (error) {
    logger.error('Error updating multiplayer game:', error);
    return { success: false, error: 'Failed to update game state' };
  }
}

/**
 * End a multiplayer game
 */
export async function endMultiplayerGame(
  gameId: string,
  winnerId: string | null
): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await completeGame(gameId, winnerId);

    if (result.success) {
      // Create match history record
      await createMatchHistoryRecord(gameId);
    }

    return result;
  } catch (error) {
    logger.error('Error ending multiplayer game:', error);
    return { success: false, error: 'Failed to end game' };
  }
}

/**
 * Create match history record for completed game
 */
async function createMatchHistoryRecord(gameId: string): Promise<void> {
  try {
    const { data: game } = await supabase
      .from('crossroads_games')
      .select(`
        *,
        crossroads_game_players (
          user_id,
          player_index,
          deck_id,
          commander_health
        )
      `)
      .eq('id', gameId)
      .single();

    if (!game || !game.crossroads_game_players) return;

    const players = game.crossroads_game_players as any[];
    const player1 = players.find((p: any) => p.player_index === 0);
    const player2 = players.find((p: any) => p.player_index === 1);

    if (!player1 || !player2) return;

    const gameState = game.game_state as GameState;
    const duration = Math.floor(
      (new Date(game.completed_at).getTime() - new Date(game.started_at).getTime()) / 1000
    );

    await supabase
      .from('crossroads_match_history')
      .insert({
        game_id: gameId,
        player1_id: player1.user_id,
        player1_deck_id: player1.deck_id,
        player1_commander: gameState.players[0].commander.cardId,
        player2_id: player2.user_id,
        player2_deck_id: player2.deck_id,
        player2_commander: gameState.players[1].commander.cardId,
        winner_id: game.winner_id,
        turns_played: game.turn_number,
        duration_seconds: duration,
      });
  } catch (error) {
    logger.error('Error creating match history:', error);
  }
}

/**
 * Get list of user's active multiplayer games
 */
export async function getUserActiveMultiplayerGames(): Promise<{
  success: boolean;
  games?: ActiveMultiplayerGame[];
  error?: string;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated', games: [] };
    }

    const { data: games, error } = await supabase
      .from('crossroads_games')
      .select(`
        id,
        current_player_index,
        last_action_at,
        crossroads_game_players!inner (
          user_id,
          player_index
        )
      `)
      .eq('status', 'active')
      .order('last_action_at', { ascending: false });

    if (error) {
      logger.error('Error fetching active games:', error);
      return { success: false, error: error.message, games: [] };
    }

    // Filter to games where user is a player
    const userGames = (games || [])
      .filter((game: any) =>
        game.crossroads_game_players.some((p: any) => p.user_id === user.id)
      )
      .map((game: any) => {
        const players = game.crossroads_game_players;
        const userPlayer = players.find((p: any) => p.user_id === user.id);
        const opponentPlayer = players.find((p: any) => p.user_id !== user.id);

        return {
          gameId: game.id,
          opponentId: opponentPlayer?.user_id || 'Unknown',
          opponentName: 'Opponent', // Would need to join with users table
          playerIndex: userPlayer?.player_index || 0,
          isMyTurn: game.current_player_index === userPlayer?.player_index,
          lastActionAt: game.last_action_at,
        };
      });

    return { success: true, games: userGames };
  } catch (error) {
    logger.error('Error in getUserActiveMultiplayerGames:', error);
    return { success: false, error: 'Failed to fetch active games', games: [] };
  }
}

/**
 * Subscribe to real-time game updates
 */
export function subscribeToMultiplayerGame(
  gameId: string,
  onUpdate: (gameState: GameState) => void,
  onError?: (error: any) => void
) {
  return subscribeToGameUpdates(gameId, (payload) => {
    if (payload.game_state) {
      onUpdate(payload.game_state as GameState);
    }
  });
}

/**
 * Unsubscribe from game updates
 */
export async function unsubscribeFromMultiplayerGame(channel: any) {
  await unsubscribeFromGameUpdates(channel);
}

/**
 * Check if it's the current user's turn
 */
export async function isMyTurn(
  gameId: string,
  currentPlayerIndex: number
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: playerData } = await supabase
      .from('crossroads_game_players')
      .select('player_index')
      .eq('game_id', gameId)
      .eq('user_id', user.id)
      .single();

    if (!playerData) return false;

    return playerData.player_index === currentPlayerIndex;
  } catch (error) {
    logger.error('Error checking turn:', error);
    return false;
  }
}

/**
 * Forfeit a multiplayer game
 */
export async function forfeitGame(gameId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Get opponent
    const { data: players } = await supabase
      .from('crossroads_game_players')
      .select('user_id')
      .eq('game_id', gameId);

    if (!players || players.length !== 2) {
      return { success: false, error: 'Invalid game state' };
    }

    const opponentId = players.find(p => p.user_id !== user.id)?.user_id;
    if (!opponentId) {
      return { success: false, error: 'Opponent not found' };
    }

    // Complete game with opponent as winner
    return await endMultiplayerGame(gameId, opponentId);
  } catch (error) {
    logger.error('Error forfeiting game:', error);
    return { success: false, error: 'Failed to forfeit game' };
  }
}

export const MultiplayerGameManager = {
  createGame: createMultiplayerGame,
  joinGame: joinMultiplayerGame,
  updateGameState: updateMultiplayerGameState,
  endGame: endMultiplayerGame,
  getActiveGames: getUserActiveMultiplayerGames,
  subscribe: subscribeToMultiplayerGame,
  unsubscribe: unsubscribeFromMultiplayerGame,
  isMyTurn,
  forfeitGame,
};
